import React, { forwardRef } from 'react';
import { NewTabHint } from './NewTabHint';
import Image from 'next/image';
import HIHLink from './Link';
import styles from './HIHButton.module.scss';

interface HIHButtonProps {
  link?: string;
  onClick?: () => void;
  submit?: boolean;
  isLoading?: boolean;
  title: string;
  ariaLabel?: string;
  variant?: 'light' | 'dark' | 'secondary' | 'primary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  blank?: boolean;
  className?: string;
  showArrowAfter?: boolean;
  showArrowBefore?: boolean;
  iconAfterUrl?: string;
  iconBeforeUrl?: string;
}

const variantClass: Record<string, string> = {
  dark: styles.dark,
  primary: styles.dark,
  light: styles.dark,
  secondary: styles.secondary,
  outline: styles.outline,
};

const sizeClass: Record<string, string> = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

const HIHButton = forwardRef<HTMLButtonElement, HIHButtonProps>(
  (
    {
      link,
      onClick,
      submit = false,
      title,
      ariaLabel,
      variant = 'dark',
      blank = false,
      className,
      showArrowAfter = false,
      showArrowBefore = false,
      isLoading = false,
      size = 'lg',
      iconAfterUrl,
      iconBeforeUrl,
    },
    ref,
  ) => {
    const buttonClasses = [
      styles.hihButton,
      variantClass[variant] || styles.dark,
      sizeClass[size] || styles.lg,
      isLoading ? styles.loading : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    function renderLabel() {
      return (
        <div className={styles.label}>
          {showArrowBefore && (
            <div className={styles.arrowBefore}>
              <Image
                src="/icons/arrow_forward.svg"
                width={20}
                height={20}
                alt="Arrow forward"
                className={styles.arrowAfterIcon}
              />
            </div>
          )}
          {iconBeforeUrl && (
            <Image
              src={iconBeforeUrl}
              width={20}
              height={20}
              className={styles.iconBefore}
              alt="Icon after"
            />
          )}
          {title}
          {iconAfterUrl && (
            <Image
              src={iconAfterUrl}
              width={20}
              height={20}
              className={styles.iconAfter}
              alt="Icon after"
            />
          )}
          {showArrowAfter && (
            <div className={styles.arrowAfter}>
              <Image
                src="/icons/arrow_forward.svg"
                width={20}
                height={20}
                alt="Arrow forward"
              />
            </div>
          )}
        </div>
      );
    }

    if ((onClick || submit) && !link) {
      return (
        <button
          ref={ref}
          type={submit ? 'submit' : 'button'}
          onClick={onClick}
          className={buttonClasses}
          aria-label={ariaLabel || title}
          disabled={isLoading}
        >
          {isLoading ? <>Laden...</> : renderLabel()}
          {iconAfterUrl && (
            <Image
              src={iconAfterUrl}
              width={20}
              height={20}
              className={styles.iconAfter}
              alt="Icon after"
            />
          )}
        </button>
      );
    }

    if (link) {
      return (
        <HIHLink
          href={link}
          target={blank ? '_blank' : undefined}
          rel={blank ? 'noopener noreferrer' : undefined}
          className={buttonClasses}
          aria-label={ariaLabel || title}
          onClick={onClick}
        >
          {renderLabel()}
          {blank && <NewTabHint />}
        </HIHLink>
      );
    }

    return null;
  },
);

HIHButton.displayName = 'HIHButton';

export default HIHButton;
